import { Head, Link } from "@inertiajs/react"
import { ChevronLeft, Eye } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { StatusHistoryTimeline } from "@/components/candidates/status-history-timeline"
import { CreatorAnalyticsTabs } from "@/components/crew/creator-analytics-tabs"
import { CreatorBadgesSection } from "@/components/crew/creator-badges-section"
import { CreatorCompletedMasterclassesSection } from "@/components/crew/creator-completed-masterclasses-section"
import { CreatorProfileCard } from "@/components/crew/creator-profile-card"
import { CrewMemberActionBar } from "@/components/crew/crew-member-action-bar"
import { CrewMemberCollabs } from "@/components/crew/crew-member-collabs-section"
import { CrewMemberNotesEditor } from "@/components/crew/crew-member-notes-editor"
import { CrewMemberStatusBadge } from "@/components/crew/crew-member-status-badge"
import { CrewMemberTagsEditor } from "@/components/crew/crew-member-tags-editor"
import { CrewRankTile } from "@/components/crew/crew-rank-tile"
import { CrewStatTile } from "@/components/crew/crew-stat-tile"
import { EngagementRateEditor } from "@/components/crew/engagement-rate-editor"
import { KudoComposer } from "@/components/crew/kudo-composer"
import { LifecycleAlertBadge } from "@/components/crew/lifecycle-alert-badge"
import { RecentKudosSection } from "@/components/crew/recent-kudos-section"
import { CrewMemberMessageButton } from "@/components/messaging/crew-member-message-button"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useOnline } from "@/hooks/use-online"
import AppLayout from "@/layouts/app-layout"
import {
  analyticsCrewMembershipPath,
  crewMembershipPath,
  crewPath,
  profilePreviewCrewMembershipPath,
} from "@/routes"
import type { CreatorShowProps, CrewMembershipStatus } from "@/types/crew"

/**
 * OKS-26 — `pages/Brand/Crew/Show.tsx` (Creator detail, brand view).
 *
 * Inertia page consuming the BE-locked `CreatorShowProps` shape:
 *   - **Nested `membership: { creator: {...} }`** root (BE rev-5 lock).
 *   - **URL-keyed `actions`** (hide-when-null discriminator) — every
 *     transition button DOM-absent when its URL is null.
 *   - **`status_history` 4-key shape**: `{from, to, actor: {id, name} | null, at}`.
 *     Null-actor rows render italic muted "System" / "Système" via
 *     `crew.detail.status_history.actor_system`.
 *
 * **5 states** (UX Spec V2 §States):
 *   - Loading — first paint only via `<CrewMemberDetailSkeleton>`.
 *     Status transitions get button-local spinners (NO full-page
 *     overlay) per UX V2 lock.
 *   - Empty — per-section: notes/tags/engagement empty placeholders;
 *     status history → ≥1 synthetic row from BE; collabs → the member's
 *     collabs (OKS-1038, deferred) or `<CollabHistoryEmpty>` when none.
 *   - Error — page fetch rejected → handled by parent boundary; save
 *     errors surface field-level via Inertia `errors`.
 *   - Success — PATCH/transition ok; toast via `useFlash` (mounted at
 *     `PersistentLayout`, NOT here — OKS-13/18 lock).
 *   - Offline — `<navigator.onLine === false>` surfaces sticky banner
 *     above the page; submits + transitions disabled.
 *
 * **Optimistic status badge** — `CrewMemberActionBar` flips the local
 * status state on click; reverts on `onError`. Status history row
 * prepends server-side after the redirect re-renders the page.
 *
 * **Archive redirects to `/crew`** (BE 303); flash success toast renders
 * on the roster page via `useFlash`. No in-page success toast here.
 *
 * **`useFlash` is mounted** at the layout level via `PersistentLayout`
 * — this page does NOT mount it again (would double-toast).
 *
 * **Locked testids** (`creator-detail-*` prefix):
 *   - `creator-detail-page` · `-back` · `-handle` · `-status-badge` ·
 *     `-tiktok-link` (rendered inside `<CreatorProfileCard>`)
 *   - all section-specific testids owned by child components
 *   - `creator-detail-error` · `-loading` · `-offline-banner` for the 5 states
 */
export default function CrewShow({
  membership,
  status_history,
  brand_tags,
  actions,
  collab_history,
  completed_masterclasses,
  can_edit,
  recent_kudos,
  can_send_kudo,
  badges,
  back_url,
}: CreatorShowProps) {
  const { t, i18n } = useTranslation()
  const online = useOnline()

  /**
   * Resolve a crew membership status to its localized label via
   * literal `t("...")` calls in a switch — pattern from
   * `candidate-status-badge.tsx` `candidateStatusLabel`. Dynamic
   * `t(varKey)` calls don't survive the inertia_i18n_health_spec
   * scanner; the switch keeps every key statically traceable.
   */
  const crewStatusLabel = (status: string): string => {
    switch (status) {
      case "pending_charter":
        return t("crew.statuses.pending_charter")
      case "active":
        return t("crew.statuses.active")
      case "paused":
        return t("crew.statuses.paused")
      case "archived":
        return t("crew.statuses.archived")
      default:
        return status
    }
  }

  // Optimistic status badge swap. Snapshot tracks `(optimistic,
  // serverStatus)` together so a server-prop update naturally clears
  // any stale optimism without a `useEffect` (React docs §"Adjusting
  // some state when a prop changes" — a setState during render is the
  // sanctioned pattern; cheaper than the effect alternative). After a
  // successful transition Inertia re-renders the page with the new
  // server `membership.status`; the inline reset below detects that
  // and drops the local optimism.
  const [snapshot, setSnapshot] = useState<{
    optimistic: CrewMembershipStatus | null
    serverStatus: CrewMembershipStatus
  }>({ optimistic: null, serverStatus: membership.status })

  if (snapshot.serverStatus !== membership.status) {
    setSnapshot({ optimistic: null, serverStatus: membership.status })
  }

  const status = snapshot.optimistic ?? membership.status

  const setOptimisticStatus = (next: CrewMembershipStatus) =>
    setSnapshot((prev) => ({ ...prev, optimistic: next }))
  const revertOptimisticStatus = () =>
    setSnapshot((prev) => ({ ...prev, optimistic: null }))

  const backHref = back_url || crewPath()

  return (
    <AppLayout>
      <Head
        title={t("crew.detail.head_title", {
          handle: membership.creator.tiktok_handle,
        })}
      />
      <div
        data-testid="creator-detail-page"
        data-status={status}
        // **Mobile sticky-footer composition** (UX V2 §Responsive +
        // §Action bar). At <640px the action bar renders fixed at the
        // viewport bottom (see `<CrewMemberActionBar>` className
        // below); the extra `pb-24` here keeps the last card off the
        // bar's z-index. ≥640 the bar lives inline at the top-right
        // of the header and the page returns to its usual padding.
        className="flex flex-col gap-6 p-6 pb-24 sm:pb-6"
      >
        <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Link
              href={backHref}
              data-testid="creator-detail-back"
              className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-sm"
            >
              <ChevronLeft className="size-4" aria-hidden="true" />
              {t("crew.detail.back")}
            </Link>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link href={crewPath()}>
                      {t("crew.detail.breadcrumb_root")}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>
                    <h1
                      data-testid="creator-detail-handle"
                      className="truncate font-mono text-xl font-semibold"
                    >
                      @{membership.creator.tiktok_handle}
                    </h1>
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            <div className="flex flex-wrap items-center gap-2">
              <CrewMemberStatusBadge
                status={status}
                testId="creator-detail-status-badge"
              />
              {/* OKS-27 — Lifecycle alert bar (UX V2 §Header strip).
                  Defense-in-depth: badges only render when status is
                  active, even if BE regresses and ships non-null
                  alerts on a non-active membership. UX V2 lock:
                  stacking order Inactivity → Rotation, severity
                  descending; both share `creator-detail-alert-bar`
                  parent testid. */}
              {status === "active" && (
                <span
                  data-testid="creator-detail-alert-bar"
                  className="flex flex-wrap items-center gap-1.5"
                >
                  <LifecycleAlertBadge
                    variant="inactivity"
                    alert={membership.lifecycle_alerts.inactivity}
                    testId="creator-detail-alert-inactivity"
                    tooltipTestId="creator-detail-alert-inactivity-tooltip"
                  />
                  <LifecycleAlertBadge
                    variant="rotation"
                    alert={membership.lifecycle_alerts.rotation}
                    testId="creator-detail-alert-rotation"
                    tooltipTestId="creator-detail-alert-rotation-tooltip"
                  />
                </span>
              )}
            </div>
          </div>
          {/*
            OKS-37 — "Message" navigation shortcut to the
            conversation surface for this membership. Hide-when-
            null: visible iff at least one transition action is
            allowed (`update_url` is non-null when the caller has
            the brand/agency-active write floor — same gate as
            ConversationPolicy.create_message?). Sits left of the
            action bar at ≥640px; inline above on mobile.
          */}
          {/* OKS-400 — read-only "Preview profile" entry. Opens the
              creator's own profile (rank/XP/badges/activity) as they
              see it, in a read-only preview. Available to any caller who
              can view this detail page. */}
          <Button
            asChild
            variant="outline"
            size="sm"
            className="hidden sm:inline-flex"
          >
            <Link
              href={profilePreviewCrewMembershipPath(membership.id)}
              data-testid="creator-detail-profile-preview"
            >
              <Eye aria-hidden="true" className="size-4" />
              {t("crew.profile_preview.entry")}
            </Link>
          </Button>
          <CrewMemberMessageButton
            crewMembershipId={membership.id}
            canMessage={actions.update_url !== null}
            className="hidden sm:inline-flex"
          />
          <CrewMemberActionBar
            actions={actions}
            handle={membership.creator.tiktok_handle}
            onOptimisticTransition={setOptimisticStatus}
            onOptimisticRevert={revertOptimisticStatus}
            // Sticky-footer at <640px (UX V2 §Responsive); inline
            // header-right at ≥640px. The `fixed` positioning keeps
            // the bar visible when the user scrolls long status
            // history / collab cards on mobile — critical on
            // `pending_charter` where Archive is the only legal
            // action and would otherwise scroll out of reach.
            // `border-t / bg-background` give the bar a chrome
            // separation from the page content underneath; page
            // container ships `pb-24 sm:pb-6` to keep the last card
            // out of the bar's z-index.
            className="bg-background fixed right-0 bottom-0 left-0 z-30 justify-end border-t px-4 py-3 sm:static sm:flex-shrink-0 sm:justify-start sm:border-0 sm:bg-transparent sm:px-0 sm:py-0"
          />
        </header>

        {/* OKS-64 — 2-tab strip (Overview · Analytics). "Overview"
            is active on this page; the Analytics tab links to
            `/crew/:id/analytics`. URL-driven so deep-linking +
            browser back-button preserve the active tab. */}
        <CreatorAnalyticsTabs
          active="overview"
          overviewPath={crewMembershipPath(membership.id)}
          analyticsPath={analyticsCrewMembershipPath(membership.id)}
        />

        {!online && (
          <p
            data-testid="creator-detail-offline-banner"
            role="status"
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
          >
            {t("crew.detail.states.offline_banner")}
          </p>
        )}

        {/* OKS-1038 — `min-w-0` on BOTH columns: a grid item defaults to
            `min-width: auto`, so below `lg` (one auto track) a long nowrap
            descendant (a truncated mission title) sized the track to its
            min-content and pushed the column past a 375px viewport. */}
        <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
          <div
            className="flex min-w-0 flex-col gap-6"
            data-testid="creator-detail-main-column"
          >
            <CreatorProfileCard membership={membership} />
            {/* OKS-1038 — the member's collabs, directly under the profile
                card and above Badges (UX Spec §2: the operational list is
                the reason to open this page; on mobile it would otherwise sit
                under five cards). Deferred prop — see the component. */}
            <CrewMemberCollabs
              membershipId={membership.id}
              history={collab_history}
            />
            {/* OKS-969 — earned-badge medallions. Directly above Kudos:
                earned recognition then authored recognition (BRIEF D1).
                Renders its own empty state, so it is not conditional. */}
            <CreatorBadgesSection badges={badges} />
            {/* OKS-396 — Kudos card: inline composer (gated by
                `can_send_kudo`) above the RECENT KUDOS strip (UX Spec
                §2). Placed directly above the notes card. RECENT KUDOS
                always renders (even when the viewer can't send); only
                the composer is permission-gated. Success toast surfaces
                via `useFlash` at PersistentLayout — NOT mounted here. */}
            <Card data-testid="creator-detail-kudos-section">
              <CardHeader>
                <CardTitle>{t("crew.detail.kudos.section_title")}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-6">
                {can_send_kudo && <KudoComposer membership={membership} />}
                <RecentKudosSection
                  recentKudos={recent_kudos}
                  creatorName={membership.creator.display_name}
                />
              </CardContent>
            </Card>
            <CrewMemberNotesEditor
              membership={membership}
              updateUrl={actions.update_url}
              canEdit={can_edit}
            />
            <StatusHistoryTimeline
              events={status_history}
              formatEventLabel={(from, to) => {
                const fromLabel =
                  from === null
                    ? t("crew.detail.status_history.created_event_from")
                    : crewStatusLabel(from)
                return t("crew.detail.status_history.transition_format", {
                  from: fromLabel,
                  to: crewStatusLabel(to),
                })
              }}
              nullActorLabel={t("crew.detail.status_history.actor_system")}
              nullActorItalic
              sectionTitle={t("crew.detail.status_history.section_title")}
              dotColorMap={CREW_DOT_COLOR_MAP}
              testIdRoot="creator-detail-status"
            />
            {/* OKS-57 — Per-creator Masterclasses-completed card.
                Slotted in the left column after StatusHistory (the collab
                card moved to the top in OKS-1038). */}
            <CreatorCompletedMasterclassesSection
              payload={completed_masterclasses}
            />
          </div>
          <div
            className="flex min-w-0 flex-col gap-6"
            data-testid="creator-detail-side-column"
          >
            <EngagementRateEditor
              engagementRate={membership.engagement_rate}
              updateUrl={actions.update_url}
              canEdit={can_edit}
            />
            {/* OKS-48 — Read-only XP balance tile (UX Spec §2.4 —
                positioned after engagement rate). Reuses the existing
                CrewStatTile primitive so the visual treatment matches
                the OKS-25 quick-stats strip + the row chip's empty-
                state semantics ("XP earned: 0", never `—`). */}
            <CrewStatTile
              testId="creator-detail-stat-xp"
              label={t("xp.tile.label")}
              value={new Intl.NumberFormat(i18n.language || "fr").format(
                membership.xp_balance,
              )}
              rawValue={membership.xp_balance}
            />
            {/* OKS-49 — Read-only Rank tile (UX Spec §2.3 — sibling of
                the XP tile, positioned immediately after). Suppressed
                when `membership.rank` is null (defensive — production
                brands always have the default ladder seeded). */}
            {membership.rank && <CrewRankTile name={membership.rank.name} />}
            <CrewMemberTagsEditor
              tags={membership.tags}
              brandTags={brand_tags}
              updateUrl={actions.update_url}
              canEdit={can_edit}
            />
          </div>
        </div>
      </div>
    </AppLayout>
  )
}

/**
 * 4-value crew status → Tailwind dot-color map. Mirrors the
 * `<CrewMemberStatusBadge>` palette so the timeline dot reads as
 * "same status". Static at module-scope — palette is locale-
 * independent.
 */
const CREW_DOT_COLOR_MAP: Record<CrewMembershipStatus, string> = {
  pending_charter: "bg-amber-400",
  active: "bg-emerald-400",
  paused: "bg-zinc-400",
  archived: "bg-zinc-500",
}

// Re-export the skeleton so Inertia's lazy-prop loaders can show it
// during a partial-reload of the page (consumers: progressive
// hydration in V2). Not used at first paint — Inertia ships
// server-rendered props.
export { CrewMemberDetailSkeleton } from "@/components/crew/crew-member-detail-skeleton"
